import { resolveCookieDomainAttribute } from './cookie-domain';

const ATTRIBUTION_COOKIE_NAME = 'ef_marketing_attribution_v1';
const ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;
const ATTRIBUTION_QUERY_KEYS = [
  'fbclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

type AttributionQueryKey = (typeof ATTRIBUTION_QUERY_KEYS)[number];

export type MarketingAttributionParams = Partial<Record<AttributionQueryKey, string>>;

export type MarketingAttributionTouchpoint = {
  params: MarketingAttributionParams;
  capturedAt: string;
  page: string;
  host: string;
};

export type StoredMarketingAttribution = {
  version: 1;
  firstTouch: MarketingAttributionTouchpoint;
  latestTouch: MarketingAttributionTouchpoint;
};

function getCookie(name: string) {
  if (typeof document === 'undefined') {
    return null;
  }

  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      return part.slice(prefix.length);
    }
  }

  return null;
}

function parseCookieValue(rawValue: string | null): StoredMarketingAttribution | null {
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(decodeURIComponent(rawValue)) as StoredMarketingAttribution;
  } catch (_error) {
    return null;
  }
}

function resolveCookieDomain() {
  if (typeof window === 'undefined') {
    return '';
  }

  return resolveCookieDomainAttribute(window.location.hostname);
}

function buildTouchpoint(params: MarketingAttributionParams): MarketingAttributionTouchpoint {
  return {
    params,
    capturedAt: new Date().toISOString(),
    page: window.location.pathname + window.location.search,
    host: window.location.hostname,
  };
}

function setCookie(value: StoredMarketingAttribution) {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const domain = resolveCookieDomain();
  document.cookie =
    `${ATTRIBUTION_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(value))}`
    + `; path=/; max-age=${ATTRIBUTION_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${domain}${secure}`;
}

export function clearMarketingAttributionCookie() {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const domain = resolveCookieDomain();
  document.cookie = `${ATTRIBUTION_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax${domain}${secure}`;
}

export function extractMarketingAttribution(search: string) {
  const searchParams = new URLSearchParams(search);
  const params: MarketingAttributionParams = {};

  for (const key of ATTRIBUTION_QUERY_KEYS) {
    const value = searchParams.get(key);
    if (value) {
      params[key] = value;
    }
  }

  return params;
}

export function readMarketingAttributionCookie() {
  return parseCookieValue(getCookie(ATTRIBUTION_COOKIE_NAME));
}

export function captureMarketingAttribution(search: string) {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = extractMarketingAttribution(search);
  if (Object.keys(params).length === 0) {
    return readMarketingAttributionCookie();
  }

  const existing = readMarketingAttributionCookie();
  const latestTouch = buildTouchpoint(params);
  const nextValue: StoredMarketingAttribution = {
    version: 1,
    firstTouch: existing?.firstTouch ?? latestTouch,
    latestTouch,
  };

  setCookie(nextValue);
  return nextValue;
}

export const marketingAttribution = {
  cookieName: ATTRIBUTION_COOKIE_NAME,
  cookieMaxAgeSeconds: ATTRIBUTION_COOKIE_MAX_AGE_SECONDS,
  queryKeys: ATTRIBUTION_QUERY_KEYS,
};
